import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  VaModal,
  VaRadio,
  VaAlert,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import _ from 'platform/utilities/data';
import FormNavButtons from 'platform/forms-system/src/js/components/FormNavButtons';
import { scrollToFirstError } from 'platform/utilities/scroll';
import { focusElement } from 'platform/utilities/ui';
import { checkValidations } from '../../shared/utils/validations';
import {
  getVaEvidence,
  getPrivateFacilities,
  getPrivateEvidenceUploads,
} from '../utils';
import { updateFormData as deriveFromLegacy } from '../pages/evidenceRequest';
import {
  evidenceRequestAdditionalInfo,
  medicalEvidencePactAdditionalInfo,
  modalRemovalEvidenceNote,
  evidenceRequestQuestion,
  privateEvidenceContent,
  privateEvidenceContentCombined,
  vaEvidenceContent,
  privateFacilityContent,
  privateFacilityContentCombined,
  alertMessage,
  renderFacilityList,
  renderFileList,
  missingSelectionErrorMessageEvidenceRequestPage,
} from '../content/evidenceRequest';

export const EvidenceRequestPage = ({
  data,
  setFormData,
  onReviewPage,
  contentBeforeButtons,
  contentAfterButtons,
  goBack,
  goForward,
  updatePage,
}) => {
  const selectionField = 'view:hasMedicalRecords';
  const hasMedicalRecords = _.get('view:hasMedicalRecords', data, null);
  const [hasError, setHasError] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertType, setAlertType] = useState([]);

  // Derive view:hasMedicalRecords from legacy IPF fields on first render only.
  const mountDataRef = useRef(data);
  const hasDerivedRef = useRef(false);
  useEffect(() => {
    if (hasDerivedRef.current) {
      return;
    }
    hasDerivedRef.current = true;
    const d = mountDataRef.current;
    const derived = deriveFromLegacy(d, d);
    if (derived !== d) {
      setFormData(derived);
    }
  }, [setFormData]);

  useEffect(() => {
    if (hasError) {
      scrollToFirstError();
    }
  }, [hasError]);

  const hasEvidenceToRemove = () => {
    return (
      getVaEvidence(data).length > 0 ||
      getPrivateEvidenceUploads(data).length > 0 ||
      getPrivateFacilities(data).length > 0
    );
  };
  const missingSelection = (error, _fieldData, formData) => {
    const value = formData?.[selectionField];
    if (value !== true && value !== false) {
      error.addError?.(missingSelectionErrorMessageEvidenceRequestPage);
    }
  };

  const checkErrors = (formData = data) => {
    const error = checkValidations(
      [missingSelection],
      data?.[selectionField],
      formData,
    );

    const result = error?.[0] || null;
    setHasError(result);
    return result;
  };

  const mapFormDataToLegacy = formData => {
    const selectableEvidenceTypes =
      formData['view:selectableEvidenceTypes'] || {};
    const hasVaMedicalRecords =
      selectableEvidenceTypes['view:hasVaMedicalRecords'] === true;
    const hasPrivateMedicalRecords =
      selectableEvidenceTypes['view:hasPrivateMedicalRecords'] === true;
    const hasOtherEvidence =
      selectableEvidenceTypes['view:hasOtherEvidence'] === true;
    return hasVaMedicalRecords || hasPrivateMedicalRecords || hasOtherEvidence;
  };

  const handlers = {
    onChangeAndRemove: () => {
      const updatedFormData = { ...data };
      const selectableEvidenceTypes = {
        ...(updatedFormData['view:selectableEvidenceTypes'] || {}),
      };

      if (getVaEvidence(data).length > 0) {
        delete updatedFormData.vaTreatmentFacilities;
        selectableEvidenceTypes['view:hasVaMedicalRecords'] = false;
        setAlertType(prevState => [...prevState, 'va']);
      }
      if (getPrivateEvidenceUploads(data).length > 0) {
        delete updatedFormData.privateMedicalRecordAttachments;
        selectableEvidenceTypes['view:hasPrivateMedicalRecords'] = false;
        setAlertType(prevState => [...prevState, 'privateMedicalRecords']);
      }
      if (getPrivateFacilities(data).length > 0) {
        delete updatedFormData.providerFacility;
        selectableEvidenceTypes['view:hasPrivateMedicalRecords'] = false;
        setAlertType(prevState => [...prevState, 'privateFacility']);
      }
      updatedFormData['view:selectableEvidenceTypes'] = selectableEvidenceTypes;
      updatedFormData['view:hasEvidence'] =
        mapFormDataToLegacy(updatedFormData);
      setFormData(updatedFormData);
      setModalVisible(false);
      /**
        thread for reason to use setTimeout here
        @see https://dsva.slack.com/archives/C01DBGX4P45/p1774282809644219
      */
      setTimeout(() => {
        // focus on the 'continue' button
        focusElement('.usa-button-primary');
        // followed by setting the alert to be visible
        setAlertVisible(true);
      }, 100);
    },
    onCancelChange: () => {
      const updatedFormData = {
        ...data,
        [selectionField]: true,
        'view:hasEvidence': true,
      };
      setFormData(updatedFormData);
      setModalVisible(false);
    },
    onSelection: event => {
      const { value } = event?.detail || {};
      const booleanValue = value === true || value === 'true';
      const selectableEvidenceTypes =
        data['view:selectableEvidenceTypes'] || {};
      const hasOtherEvidence =
        selectableEvidenceTypes['view:hasOtherEvidence'] === true;

      const hasAnyEvidence = booleanValue || hasOtherEvidence;
      const formData = {
        ...data,
        [selectionField]: booleanValue,
        'view:hasEvidence': hasAnyEvidence,
      };
      setFormData(formData);
      checkErrors(formData);
    },
    onSubmit: event => {
      event.preventDefault();
      /*
        checkErrors is handled by the useEffect above to trigger the expected behavior
        of reading the error message via screen reader and focus on the va-radio-button
      */
      if (checkErrors()) {
        return;
      }
      if (hasMedicalRecords === false && hasEvidenceToRemove()) {
        setModalVisible(true);
      } else if (hasMedicalRecords === false) {
        const updatedFormData = { ...data };
        updatedFormData['view:selectableEvidenceTypes'] = {
          ...(updatedFormData['view:selectableEvidenceTypes'] || {}),
          'view:hasVaMedicalRecords': false,
          'view:hasPrivateMedicalRecords': false,
        };
        const selectableEvidenceTypes =
          data['view:selectableEvidenceTypes'] || {};
        const hasOtherEvidence =
          selectableEvidenceTypes['view:hasOtherEvidence'] === true;
        updatedFormData['view:hasEvidence'] = hasOtherEvidence;
        updatedFormData.patient4142Acknowledgement = false;
        setFormData(updatedFormData);
        setAlertVisible(false);
        goForward({ formData: updatedFormData });
      } else {
        // hasMedicalRecords is true, so hasEvidence must be true
        const syncedData = {
          ...data,
          'view:hasEvidence': true,
        };
        setFormData(syncedData);
        setAlertVisible(false);
        goForward({ formData: syncedData });
      }
    },
    onUpdatePage: event => {
      event.preventDefault();
      if (checkErrors()) {
        return;
      }
      if (hasMedicalRecords === false && hasEvidenceToRemove()) {
        setModalVisible(true);
      } else {
        setAlertVisible(false);
        updatePage(event);
      }
    },
  };

  return (
    <>
      <h3>Medical records that support your disability claim</h3>
      {alertVisible && (
        <div className="vads-u-margin-bottom--1">
          <VaAlert
            role="alert"
            closeBtnAriaLabel="Close notification"
            closeable
            onCloseEvent={() => setAlertVisible(false)}
            fullWidth="false"
            slim
            status="success"
            visible={alertVisible}
            uswds
          >
            <p className="vads-u-margin-y--0">{alertMessage(alertType)}</p>
          </VaAlert>
        </div>
      )}
      <VaModal
        clickToClose
        modalTitle="Change your medical records?"
        onCloseEvent={handlers.onCancelChange}
        onPrimaryButtonClick={handlers.onChangeAndRemove}
        onSecondaryButtonClick={handlers.onCancelChange}
        visible={modalVisible}
        status="warning"
        primaryButtonText={
          getPrivateEvidenceUploads(data).length > 0
            ? 'Change and delete'
            : 'Change and remove'
        }
        secondaryButtonText="Cancel change"
      >
        {getVaEvidence(data).length > 0 && (
          <>
            {vaEvidenceContent}
            {renderFacilityList(getVaEvidence(data), 'treatmentCenterName')}
          </>
        )}
        {getPrivateFacilities(data).length > 0 && (
          <>
            {getVaEvidence(data).length > 0
              ? privateFacilityContentCombined
              : privateFacilityContent}
            {renderFacilityList(
              getPrivateFacilities(data),
              'providerFacilityName',
            )}
          </>
        )}
        {getPrivateEvidenceUploads(data).length > 0 && (
          <>
            {getVaEvidence(data).length > 0 ||
            getPrivateFacilities(data).length > 0
              ? privateEvidenceContentCombined
              : privateEvidenceContent}
            {renderFileList(getPrivateEvidenceUploads(data))}
          </>
        )}
        {data?.disabilityCompConditionsEvidenceMessagingTest &&
          modalRemovalEvidenceNote}
      </VaModal>
      <form onSubmit={handlers.onSubmit}>
        <VaRadio
          label={evidenceRequestQuestion.label}
          hint={evidenceRequestQuestion.hint}
          required
          uswds="true"
          class="rjsf-web-component-field hydrated"
          aria-invalid={hasError ? 'true' : 'false'}
          onVaValueChange={handlers.onSelection}
          error={hasError}
        >
          <va-radio-option
            label="Yes"
            name="private"
            checked={hasMedicalRecords === true}
            value="true"
          />
          <va-radio-option
            label="No"
            name="private"
            checked={hasMedicalRecords === false}
            value="false"
          />
        </VaRadio>
        {!onReviewPage &&
          (data?.disabilityCompConditionsEvidenceMessagingTest
            ? medicalEvidencePactAdditionalInfo
            : evidenceRequestAdditionalInfo)}

        {onReviewPage ? (
          /**
           * Does not use web component for design consistency on all pages.
           * @see https://va.ghe.com/software/vets-website/pull/35911
           */
          // eslint-disable-next-line @department-of-veterans-affairs/prefer-button-component
          <button
            className="usa-button-primary"
            type="button"
            onClick={event => handlers.onUpdatePage(event)}
          >
            Update page
          </button>
        ) : (
          <>
            {contentBeforeButtons}
            <FormNavButtons
              goBack={goBack}
              goForward={handlers.onSubmit}
              submitToContinue
              useWebComponents
              enableAnalytics
            />
            {contentAfterButtons}
          </>
        )}
      </form>
    </>
  );
};

EvidenceRequestPage.propTypes = {
  contentAfterButtons: PropTypes.element,
  contentBeforeButtons: PropTypes.element,
  data: PropTypes.object,
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  setFormData: PropTypes.func,
  updatePage: PropTypes.func,
  onReviewPage: PropTypes.bool,
};
